import * as React from "react";
import {
  Grid,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
} from "@mui/material";

export interface Props {
  label: string;
  selectedDisplayValues: any[];
  selectedStateValues: any[];
  selectedInsuranceFunds: any[];
  selectedCharterTypes: any[];
  selectedTransactionTypes: any[];
  choices: FormSelectChoice[];
  onAdd: (
    changed: any[],
    currentReport: string,
    filterField?: string,
    updateReport?: boolean,
  ) => void;
  onRemove: (
    changed: any[],
    currentReport?: string,
    filterField?: string,
    updateReport?: boolean,
  ) => void;
  currentReport: string;
  filterField?: string;
  updateReport?: boolean;
}

interface FormSelectChoice {
  label: string;
  value: any;
  checked?: boolean;
  isState?: boolean;
}

function SelectSection({
  label,
  selectedDisplayValues,
  selectedStateValues,
  selectedInsuranceFunds,
  selectedCharterTypes,
  selectedTransactionTypes,
  choices,
  onAdd,
  onRemove,
  currentReport,
  filterField,
  updateReport,
}: Props) {
  selectedDisplayValues = selectedDisplayValues.filter((item) => {
    return item !== "";
  });
  const annualDataSummariesReportTypes = ["CBS", "CBF", "SIS", "SIF"];
  const locationsDisplay =
    label === "Locations by States & Territories" &&
    annualDataSummariesReportTypes.includes(currentReport);

  function getSelectedValues() {
    const fieldValues: any = {
      selectedStates: selectedStateValues,
      selectedStatesAlpha: selectedStateValues,
      selectedInsuranceFunds: selectedInsuranceFunds,
      selectedCharterTypes: selectedCharterTypes,
      selectedTransactionTypes: selectedTransactionTypes,
    };
    return filterField ? fieldValues[filterField] : selectedDisplayValues;
  }

  const selectedAll = choices.every(
    (choice: FormSelectChoice) => choice.checked,
  );
  const selectAllLabel = `${label}_Select All / None`;
  const allValues = choices.map((choice: any) => {
    return choice.value;
  });

  const selectAllChoice = {
    label: "Select All / None",
    value: selectAllLabel,
    checked: selectedAll,
  };
  const choiceChanged = (event: any) => {
    if (event.target.checked) {
      if (event.target.value === selectAllLabel) {
        onAdd(allValues, currentReport, filterField, updateReport);
      } else {
        onAdd(
          [
            isNaN(event.target.value)
              ? event.target.value
              : parseInt(event.target.value),
          ],
          currentReport,
          filterField,
          updateReport,
        );
      }
    } else {
      if (event.target.value === selectAllLabel) {
        onRemove(allValues, currentReport, filterField, updateReport);
      } else {
        onRemove(
          [
            isNaN(event.target.value)
              ? event.target.value
              : parseInt(event.target.value),
          ],
          currentReport,
          filterField,
          updateReport,
        );
      }
    }
  };

  const aggChoices = choices.filter((choice) =>
    [0, 99, 98].includes(choice.value),
  );
  const individualChoices = choices.filter(
    (choice) => ![0, 99, 98].includes(choice.value),
  );
  const allChoices = [
    selectAllChoice,
    ...choices.map((c: any) => {
      c.checked = getSelectedValues()
        ? getSelectedValues().indexOf(c.value) > -1
        : false;
      return c;
    }),
  ];

  const aggGroup = (
    <>
      <FormGroup>
        <Typography
          variant="h3"
          sx={{ paddingLeft: 0, paddingBottom: 0, fontSize: 14 }}
        >
          Aggregated Selections
        </Typography>
        {aggChoices.map((choice: any, index: number) => {
          // ID prop to allow searching for input selection
          const id = choice.label
            .replace(/[^a-zA-Z ]/g, "")
            .replace(/\s\s+/g, " ")
            .replace(/\s/g, "-")
            .toLowerCase();
          const labelId = `${id}-${index}-label`;
          return (
            <FormControlLabel
              key={labelId}
              id={labelId}
              label={choice.label}
              sx={{
                "maxWidth": 225,
                "maxHeight": 30,
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  color: "#1A4480",
                },
              }}
              control={
                <Checkbox
                  onClick={choiceChanged}
                  color="primary"
                  checked={choice.checked}
                  slotProps={{
                    input: {
                      "aria-labelledby": labelId,
                      "id": `${id}-${choice.checked}`,
                    },
                  }}
                  value={choice.value}
                  id={id}
                />
              }
            />
          );
        })}
      </FormGroup>
    </>
  );
  const selectAllId = selectAllChoice.label
    .replace(/[^a-zA-Z ]/g, "")
    .replace(/\s\s+/g, " ")
    .replace(/\s/g, "-")
    .toLowerCase();
  const selectAllLabelId = `${selectAllId}-${0}-label`;

  // TODO: Want to make this uniform with the other checkbox component (<Choice /> in ./SubSectionFields.tsx) used in Historical App.
  // ChoiceList does not allow for class to be applied to root <div> or for size adjustment on Checkbox type
  const val = (
    <Grid container>
      <FormControl component="fieldset">
        {locationsDisplay && (
          <>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 400,
                pl: 1,
                paddingBottom: 0,
                fontSize: "16px",
              }}
            >
              Filter results with these selections.
            </Typography>
            <FormControlLabel
              key={selectAllLabelId}
              id={selectAllLabelId}
              label={selectAllChoice.label}
              sx={{
                "paddingLeft": 1,
                "& .MuiFormControlLabel-label": {
                  color: "#1A4480",
                },
              }}
              control={
                <Checkbox
                  onClick={choiceChanged}
                  color="primary"
                  checked={selectAllChoice.checked}
                  slotProps={{
                    input: {
                      "aria-labelledby": selectAllLabelId,
                      "id": `${selectAllId}-${selectAllChoice.checked}`,
                    },
                  }}
                  value={selectAllChoice.value}
                  id={selectAllLabelId}
                />
              }
            />
          </>
        )}
        {locationsDisplay && (
          <Grid container spacing={0}>
            {<Grid size={2.4}>{aggGroup}</Grid>}
            <Grid size={9.6}>
              <Typography
                variant="h3"
                sx={{
                  mt: 1.4,
                  paddingBottom: 0,
                  fontSize: 14,
                }}
              >
                Individual Selections
              </Typography>

              <Box
                sx={{
                  columnCount: 4,
                  columnGap: 0,
                  paddingBottom: "16px",
                }}
              >
                {individualChoices.map((choice: any, index: number) => {
                  // ID prop to allow searching for input selection
                  const id = choice.label
                    .replace(/[^a-zA-Z ]/g, "")
                    .replace(/\s\s+/g, " ")
                    .replace(/\s/g, "-")
                    .toLowerCase();
                  const labelId = `${id}-${index}-label`;

                  if (choice.label === "Select All / None") {
                    return null;
                  }
                  return (
                    <Grid key={index}>
                      <FormControlLabel
                        key={labelId}
                        id={labelId}
                        label={choice.label}
                        sx={{
                          "maxHeight": 30,
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                            color: "#1A4480",
                            overflowWrap: "break-word",
                            lineHeight: "12px",
                            fontWeight: "400",
                          },
                        }}
                        control={
                          <Checkbox
                            onClick={choiceChanged}
                            color="primary"
                            checked={choice.checked}
                            slotProps={{
                              input: {
                                "aria-labelledby": labelId,
                                "id": `${id}-${choice.checked}`,
                              },
                            }}
                            value={choice.value}
                            id={id}
                            sx={{
                              "overflowWrap": "break-word",
                              "lineHeight": "14px",
                              "& .MuiCheckbox-root": {
                                lineHeight: "14px",
                                padding: "0px",
                              },
                            }}
                          />
                        }
                      />
                    </Grid>
                  );
                })}
              </Box>
            </Grid>
          </Grid>
        )}
        {!locationsDisplay && (
          <FormGroup>
            {allChoices.map((choice: any, index: number) => {
              // ID prop to allow searching for input selection
              const id = choice.label
                .replace(/[^a-zA-Z ]/g, "")
                .replace(/\s\s+/g, " ")
                .replace(/\s/g, "-")
                .toLowerCase();
              const labelId = `${id}-${index}-label`;

              return (
                <FormControlLabel
                  key={labelId}
                  id={labelId}
                  label={choice.label}
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "14px",
                      color: "#1A4480",
                    },
                  }}
                  control={
                    <Checkbox
                      onClick={choiceChanged}
                      color="primary"
                      checked={choice.checked}
                      slotProps={{
                        input: {
                          "aria-labelledby": labelId,
                          "id": `${id}-${choice.checked}`,
                        },
                      }}
                      value={choice.value}
                      id={id}
                      sx={{
                        "overflowWrap": "break-word",
                        "lineHeight": "14px",
                        "& .MuiCheckbox-root": {
                          lineHeight: "14px",
                          padding: "0px",
                        },
                      }}
                    />
                  }
                />
              );
            })}
          </FormGroup>
        )}
      </FormControl>
    </Grid>
  );

  return val;
}

export default SelectSection;
